import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { apiClient } from "../../services/api";
import { webhookKeyService } from "../../services/webhookKeyService";
import { useAuth } from "../../providers/AuthProvider";
import { hasGrant } from "../../services/shared";
import {
  LeadDispatchLog,
  LeadDispatchLogsResponse,
  LeadDispatchStatus,
  Rule,
  WebhookKey,
} from "../../types";
import LeadDispatchLogDetail from "./LeadDispatchLogDetail";
import ExportCsvDialog from "./ExportCsvDialog";

const STATUS_CONFIG: Record<
  LeadDispatchStatus,
  { label: string; color: "success" | "warning" | "default" | "error" | "info" }
> = {
  dispatched: { label: "Inviato", color: "success" },
  duplicate: { label: "Duplicato", color: "warning" },
  skipped: { label: "Saltato", color: "default" },
  error: { label: "Errore", color: "warning" },
  failed: { label: "Fallito", color: "error" },
};

const PAGE_LIMIT = 50;
const MAX_RANGE_DAYS = 31;

type StatusFilter = LeadDispatchStatus | "all";

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysBetween(fromIso: string, toIso: string): number {
  const a = new Date(fromIso).getTime();
  const b = new Date(toIso).getTime();
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

export interface LogFilters {
  ruleId: string;
  status: StatusFilter;
  apiKeyId: string;
  from: string;
  to: string;
}

export default function LeadDispatchLogs() {
  const { grants } = useAuth();
  const isAdmin = hasGrant(grants, "admin");

  const today = new Date();
  const sevenAgo = new Date();
  sevenAgo.setDate(today.getDate() - 7);

  const [filters, setFilters] = useState<LogFilters>({
    ruleId: "",
    status: "all",
    apiKeyId: "",
    from: isoDate(sevenAgo),
    to: isoDate(today),
  });

  const [logs, setLogs] = useState<LeadDispatchLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rangeError, setRangeError] = useState<string | null>(null);

  const [rules, setRules] = useState<Rule[]>([]);
  const [keys, setKeys] = useState<WebhookKey[]>([]);

  const [selectedLog, setSelectedLog] = useState<LeadDispatchLog | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  // Carica autocomplete data on mount
  useEffect(() => {
    const loadAutocomplete = async () => {
      try {
        const rulesRes = await apiClient.get("/v1/lead-dispatch/rules", {
          params: { limit: 100 },
        });
        setRules(rulesRes.data?.data?.rules ?? []);
      } catch (e) {
        console.error("Errore caricamento rules:", e);
      }
      try {
        const keysRes = await webhookKeyService.list({ status: "active" });
        const all: WebhookKey[] = keysRes.data?.data?.keys ?? [];
        setKeys(all.filter((k) => (k.scopes ?? []).includes("lead-dispatch")));
      } catch (e) {
        console.error("Errore caricamento keys:", e);
      }
    };
    loadAutocomplete();
  }, []);

  const fetchLogs = useCallback(
    async (opts: { append?: boolean; cursor?: string | null } = {}) => {
      const { append = false, cursor: cursorParam = null } = opts;

      const days = daysBetween(filters.from, filters.to);
      if (days < 0) {
        setRangeError("La data di fine deve essere successiva alla data di inizio");
        return;
      }
      if (days > MAX_RANGE_DAYS) {
        setRangeError("Il range massimo è 31 giorni");
        return;
      }
      setRangeError(null);

      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = {
          limit: PAGE_LIMIT,
          from: filters.from,
          to: filters.to,
        };
        if (filters.ruleId) params.ruleId = filters.ruleId;
        if (filters.status !== "all") params.status = filters.status;
        if (filters.apiKeyId) params.apiKeyId = filters.apiKeyId;
        if (cursorParam) params.cursor = cursorParam;

        const res = await apiClient.get("/v1/lead-dispatch/logs", { params });
        const payload: LeadDispatchLogsResponse = res.data?.data ?? { logs: [] };
        const newLogs = payload.logs ?? [];
        setLogs((prev) => (append ? [...prev, ...newLogs] : newLogs));
        setCursor(payload.nextCursor ?? null);
      } catch (e) {
        console.error(e);
        setError("Errore nel caricamento dei log");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  if (!isAdmin) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", py: 3 }}>
        <Alert severity="warning">
          Solo gli amministratori possono accedere ai log globali.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1300, mx: "auto", py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
        flexWrap="wrap"
      >
        <Typography variant="h5" fontWeight={700}>
          Lead Dispatching — Log globali
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => setExportOpen(true)}
        >
          Export CSV
        </Button>
      </Stack>

      {/* Filtri */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            label="Da"
            type="date"
            size="small"
            value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="A"
            type="date"
            size="small"
            value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="logs-status">Status</InputLabel>
            <Select
              labelId="logs-status"
              label="Status"
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  status: e.target.value as StatusFilter,
                }))
              }
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="dispatched">Inviato</MenuItem>
              <MenuItem value="duplicate">Duplicato</MenuItem>
              <MenuItem value="skipped">Saltato</MenuItem>
              <MenuItem value="error">Errore</MenuItem>
              <MenuItem value="failed">Fallito</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            options={rules}
            getOptionLabel={(opt) => `${opt.displayName} (${opt.id})`}
            value={rules.find((r) => r.id === filters.ruleId) ?? null}
            onChange={(_, val) =>
              setFilters((f) => ({ ...f, ruleId: val?.id ?? "" }))
            }
            renderInput={(params) => <TextField {...params} label="Rule" />}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 240 }}
            options={keys}
            getOptionLabel={(opt) =>
              `${opt.partnerName} (${opt.keyPrefix}…)`
            }
            value={keys.find((k) => k.id === filters.apiKeyId) ?? null}
            onChange={(_, val) =>
              setFilters((f) => ({ ...f, apiKeyId: val?.id ?? "" }))
            }
            renderInput={(params) => <TextField {...params} label="API key" />}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchLogs()}
            disabled={loading}
          >
            Filtra
          </Button>
        </Stack>
        {rangeError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {rangeError}
          </Alert>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data/ora</TableCell>
                <TableCell width={120}>Status</TableCell>
                <TableCell width={120}>Tipo</TableCell>
                <TableCell>Rule / simple</TableCell>
                <TableCell width={80}>HTTP</TableCell>
                <TableCell width={100}>Durata</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Nessun log per i filtri selezionati
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {logs.map((log) => {
                const status = STATUS_CONFIG[log.status];
                return (
                  <TableRow
                    key={log.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => setSelectedLog(log)}
                  >
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {new Date(log.created_at).toLocaleString("it-IT")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status?.label ?? log.status}
                        size="small"
                        color={status?.color ?? "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.rule_type}
                        size="small"
                        variant="outlined"
                        color={log.rule_type === "simple" ? "primary" : "secondary"}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0}>
                        <Typography variant="caption" fontFamily="monospace">
                          {log.rule_id}
                        </Typography>
                        {log.simple_rule_id &&
                          log.simple_rule_id !== log.rule_id && (
                            <Typography
                              variant="caption"
                              fontFamily="monospace"
                              color="text.secondary"
                            >
                              → {log.simple_rule_id}
                            </Typography>
                          )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {log.http_status ? (
                        <Chip
                          label={log.http_status}
                          size="small"
                          variant="outlined"
                          color={log.http_status >= 400 ? "error" : "success"}
                          sx={{ fontFamily: "monospace" }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {log.duration_ms ? `${log.duration_ms}ms` : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {cursor && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            onClick={() => fetchLogs({ append: true, cursor })}
            disabled={loadingMore}
            startIcon={
              loadingMore ? <CircularProgress size={14} /> : undefined
            }
          >
            {loadingMore ? "Caricamento..." : "Carica altri"}
          </Button>
        </Box>
      )}

      <LeadDispatchLogDetail
        log={selectedLog}
        onClose={() => setSelectedLog(null)}
      />

      <ExportCsvDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        currentFilters={filters}
      />
    </Box>
  );
}
